import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import MuiBox from '@material-ui/core/Box';
import MuiButton from '@material-ui/core/Button';
import MuiTypography from '@material-ui/core/Typography';
import MuiDivider from '@material-ui/core/Divider';
import ClearIcon from '@material-ui/icons/Clear';
import GetAppIcon from '@material-ui/icons/GetApp';
import CloseIcon from '@material-ui/icons/Close';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import UndoIcon from '@material-ui/icons/Undo';
import ConfirmationDialog from '../ConfirmationDialog';

const styles = theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
  selectedText: {
    flexGrow: 1,
    paddingLeft: theme.spacing(1),
  },
  actionButton: {
    marginLeft: theme.spacing(1),
    color: theme.palette.primary.contrastText,
    borderColor: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  divider: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  },
});

/**
 * Bulk actions component for selected royalty transfers
 */
const BulkActionsRoyaltyTransfers = ({
  classes,
  selectedCount,
  selectedItems,
  onClearSelection,
  onBulkClose,
  onBulkExecute,
  onBulkUnwind,
  onBulkExport,
  loading = false,
}) => {
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    content: '',
    action: null,
  });

  // Check which operations are available for bulk actions
  const canCloseSelected = selectedItems.filter(
    item => item.can_close && !item.is_closed && !item.block_bulk_operations
  ).length;
  const canExecuteSelected = selectedItems.filter(
    item => item.can_execute_external_transfer && !item.external_transfer_id && !item.block_bulk_operations
  ).length;
  const canUnwindSelected = selectedItems.filter(
    item =>
      !item.is_closed &&
      !item.block_bulk_operations &&
      (item.expected_transfer_amount === null || item.expected_transfer_amount <= 0)
  ).length;
  const blockedOperations = selectedItems.filter(item => item.block_bulk_operations).length;

  const handleBulkClose = () => {
    if (canCloseSelected === 0) {
      return;
    }

    setConfirmDialog({
      open: true,
      title: 'Close Selected Transfers',
      content: `Are you sure you want to close ${canCloseSelected} of ${selectedCount} selected transfer${
        selectedCount !== 1 ? 's' : ''
      }? ${
        blockedOperations > 0
          ? `${blockedOperations} item${
            blockedOperations !== 1 ? 's' : ''
          } will be skipped due to blocked bulk operations.`
          : ''
      } This action cannot be undone.`,
      action: () => {
        onBulkClose();
        setConfirmDialog({ open: false, title: '', content: '', action: null });
      },
    });
  };

  const handleBulkExecute = () => {
    if (canExecuteSelected === 0) {
      return;
    }

    setConfirmDialog({
      open: true,
      title: 'Execute Selected Transfers',
      content: `Are you sure you want to execute ${canExecuteSelected} of ${selectedCount} selected transfer${
        selectedCount !== 1 ? 's' : ''
      }? ${
        blockedOperations > 0
          ? `${blockedOperations} item${
            blockedOperations !== 1 ? 's' : ''
          } will be skipped due to blocked bulk operations.`
          : ''
      } This action cannot be undone.`,
      action: () => {
        onBulkExecute();
        setConfirmDialog({ open: false, title: '', content: '', action: null });
      },
    });
  };

  const handleBulkUnwind = () => {
    if (canUnwindSelected === 0) {
      return;
    }

    setConfirmDialog({
      open: true,
      title: 'Unwind Selected Transfers',
      content: `Are you sure you want to unwind ${canUnwindSelected} of ${selectedCount} selected transfer${
        selectedCount !== 1 ? 's' : ''
      }? ${
        blockedOperations > 0
          ? `${blockedOperations} item${
            blockedOperations !== 1 ? 's' : ''
          } will be skipped due to blocked bulk operations.`
          : ''
      } This action will recalculate the transfer amounts and cannot be undone.`,
      action: () => {
        onBulkUnwind();
        setConfirmDialog({ open: false, title: '', content: '', action: null });
      },
    });
  };

  const handleBulkExport = () => {
    setConfirmDialog({
      open: true,
      title: 'Export Selected Transfers',
      content: `Are you sure you want to export ${selectedCount} selected transfer${selectedCount !== 1 ? 's' : ''}?`,
      action: () => {
        onBulkExport();
        setConfirmDialog({ open: false, title: '', content: '', action: null });
      },
    });
  };

  const handleCloseDialog = () => {
    setConfirmDialog({ open: false, title: '', content: '', action: null });
  };

  if (selectedCount === 0) return null;

  return (
    <>
      <MuiBox className={classes.root}>
        <MuiTypography variant="body2" className={classes.selectedText}>
          {selectedCount} transfer{selectedCount !== 1 ? 's' : ''} selected
          {blockedOperations > 0 && (
            <span style={{ fontSize: '0.8em', opacity: 0.8 }}> ({blockedOperations} blocked from bulk operations)</span>
          )}
        </MuiTypography>

        <MuiButton
          size="small"
          onClick={onClearSelection}
          className={classes.actionButton}
          startIcon={<ClearIcon />}
          disabled={loading}>
          Clear
        </MuiButton>

        <MuiDivider orientation="vertical" className={classes.divider} />

        <MuiButton
          size="small"
          onClick={handleBulkClose}
          className={classes.actionButton}
          startIcon={<CloseIcon />}
          disabled={loading || canCloseSelected === 0}>
          Close ({canCloseSelected})
        </MuiButton>

        <MuiButton
          size="small"
          onClick={handleBulkExecute}
          className={classes.actionButton}
          startIcon={<PlayArrowIcon />}
          disabled={loading || canExecuteSelected === 0}>
          Transfer ({canExecuteSelected})
        </MuiButton>

        {canUnwindSelected > 0 && (
          <MuiButton
            size="small"
            onClick={handleBulkUnwind}
            className={classes.actionButton}
            startIcon={<UndoIcon />}
            disabled={loading}>
            Unwind ({canUnwindSelected})
          </MuiButton>
        )}

        <MuiDivider orientation="vertical" className={classes.divider} />

        <MuiButton
          size="small"
          onClick={handleBulkExport}
          className={classes.actionButton}
          startIcon={<GetAppIcon />}
          disabled={loading}>
          Export
        </MuiButton>
      </MuiBox>

      <ConfirmationDialog
        open={confirmDialog.open}
        onClose={handleCloseDialog}
        onConfirm={confirmDialog.action}
        title={confirmDialog.title}
        content={confirmDialog.content}
        confirmText="Confirm"
        cancelText="Cancel"
        loading={loading}
      />
    </>
  );
};

BulkActionsRoyaltyTransfers.propTypes = {
  classes: PropTypes.object.isRequired,
  selectedCount: PropTypes.number.isRequired,
  selectedItems: PropTypes.array.isRequired,
  onClearSelection: PropTypes.func.isRequired,
  onBulkClose: PropTypes.func.isRequired,
  onBulkExecute: PropTypes.func.isRequired,
  onBulkUnwind: PropTypes.func.isRequired,
  onBulkExport: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

export default withStyles(styles)(BulkActionsRoyaltyTransfers);
